import React, { Suspense, lazy, useEffect } from 'react';
import { Route, useLocation } from 'react-router-dom';
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProtectedRoute from "../components/ProtectedRoute";
import { routes } from './routes';
import LoadingSpinner from '../components/LoadingSpinner';

const pages = import.meta.glob('../pages/**/*.jsx');
const routeComponents = Object.keys(pages).reduce((acc, path) => {
  const name = path
    .replace('../pages/', '')
    .replace('.jsx', '')
    .replace(/\//g, '_')
    .replace('._', '');

  acc[name.toLowerCase()] = lazy(pages[path]);
  return acc;
}, {});

const FooterNavigationScroll = () => {
  const location = useLocation();

  useEffect(() => {
    if (sessionStorage.getItem('pendingFooterScrollToTop') !== 'true') return;

    let observer;
    let timeoutId;
    let frameId;
    let innerFrameId;

    const hasMeaningfulMainContent = () => {
      const main = document.querySelector('main');
      if (!main) return true;

      const text = main.innerText.trim();
      return Boolean(text && !/^loading/i.test(text));
    };

    const completeScroll = () => {
      sessionStorage.removeItem('pendingFooterScrollToTop');
      observer?.disconnect();
      window.clearTimeout(timeoutId);
      frameId = requestAnimationFrame(() => {
        innerFrameId = requestAnimationFrame(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        });
      });
    };

    if (hasMeaningfulMainContent()) {
      completeScroll();
    } else {
      observer = new MutationObserver(() => {
        if (hasMeaningfulMainContent()) completeScroll();
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      timeoutId = window.setTimeout(completeScroll, 3000);
    }

    return () => {
      observer?.disconnect();
      window.clearTimeout(timeoutId);
      window.cancelAnimationFrame(frameId);
      window.cancelAnimationFrame(innerFrameId);
    };
  }, [location.key]);

  return null;
};

export function AppRoutes() {
  return routes.map(({ url, view, base, auth }) => {
    const Component = routeComponents[view.toLowerCase()];
    if (!Component) {
      console.error(`Component not found for view: ${view}`);
      return null;
    }

    const element = auth ? (
      <ProtectedRoute>
        <Component />
      </ProtectedRoute>
    ) : (
      <Component />
    );

    return (
      <Route
        key={url}
        path={url}
        element={
          <>
            {!base && <Header />}
            {!base ? (
              <main className="w-full min-h-[50vh] mt-[60px] lg:mt-0">
                <div className="max-w-7xl mx-auto px-[10px]">
                  <Suspense fallback={
                    <div className="absolute top-4 left-0 right-0 ">
                      <LoadingSpinner />
                    </div>
                  }>
                    {element}
                    <FooterNavigationScroll />
                  </Suspense>
                </div>
              </main>
            ) : (
              <Suspense fallback={
                <div className="absolute top-4 left-0 right-0">
                  <LoadingSpinner />
                </div>
              }>
                {element}
                <FooterNavigationScroll />
              </Suspense>
            )}
            {!base && <Footer />}
          </>
        }
      />
    );
  });
}
